import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useMemo, useState } from 'react';
import { useComplianceMatrix } from '../../../hooks/useComplianceMatrix';
import { useMultistate } from '../../../hooks/useMultistate';
import { useUpdateRequirement } from '../../../hooks/useUpdateRequirement';
import { proposalProjectApiConnect } from '../../../lib/api/proposalProjectApi.connect';
import { exportRequirements } from '../../../lib/utils/exportToExcel';
import { groupByLogicalKey } from '../v2/amendment/groupByLogicalKey';
import { HistoryToggle } from '../v2/amendment/HistoryToggle';
import { useProposalV2Context } from '../v2/ProposalV2Context';
import { ComplianceDetailDrawer } from './ComplianceDetailDrawer';
import { ComplianceMatrixTable } from './ComplianceMatrixTable';
import { ComplianceStats } from './ComplianceStats';
import { EvaluationSchemeCard } from './EvaluationSchemeCard';
import { ExportButton } from './ExportButton';
import { Filters } from './Filters';
const GROUP_BY_OPTIONS = [
    {
        key: 'none',
        label: 'No grouping'
    },
    {
        key: 'volume',
        label: 'Volume'
    },
    {
        key: 'responseAction',
        label: 'Response Action'
    },
    {
        key: 'category',
        label: 'Category'
    }
];
const RESPONSE_ACTION_GROUP_LABELS = {
    WRITE_APPROACH: 'Write Approach',
    PROVIDE_EVIDENCE: 'Provide Evidence',
    COMPLY_FORMAT: 'Comply with Format',
    SUBMIT_ACTION: 'Submit Action',
    ACKNOWLEDGE: 'Acknowledge',
    PRICE: 'Pricing',
    STAFF: 'Staffing'
};
/**
 * Extract a volume name from mapping_proposal_section.
 * "SECTION E > E.8 > D. > VOLUME II > Written Technical Solution" → "Volume II"
 * Falls back to top-level section (SECTION B, SECTION C, etc.) or "Unmapped".
 */ function deriveVolume(section) {
    if (!section) return 'Unmapped';
    const upper = section.toUpperCase();
    const volMatch = upper.match(/VOLUME\s+(I{1,3}|IV|V|VI)/);
    if (volMatch) return `Volume ${volMatch[1]}`;
    if (upper.startsWith('SECTION B')) return 'Section B — PWS/SOW';
    if (upper.startsWith('SECTION C')) return 'Section C — SOW';
    if (upper.startsWith('SECTION E')) return 'Section E — Instructions';
    return 'Other';
}
const ACTION_TABS = [
    {
        key: 'PROPOSAL_ACTION',
        label: 'Proposal Action',
        description: 'What you must do in the proposal'
    },
    {
        key: 'CONTRACT_ACTION',
        label: 'Contract Scope',
        description: 'Post-award scope requirements'
    },
    {
        key: 'GOVERNMENT_ACTION',
        label: 'Government',
        description: 'Government responsibilities'
    },
    {
        key: 'ALL',
        label: 'All',
        description: 'All requirements'
    }
];
export const ComplianceMatrixContainer = /*#__PURE__*/ memo(function ComplianceMatrixContainer({ projectId }) {
    const { setAiContext, setSidebarOpen } = useProposalV2Context();
    const [activeTab, setActiveTab] = useState('PROPOSAL_ACTION');
    const [groupBy, setGroupBy] = useState('none');
    const [selectedRequirement, setSelectedRequirement] = useState(null);
    const [filters, resetFilters, updateFilters] = useMultistate({
        searchQuery: '',
        statuses: [],
        categories: [],
        obligations: [],
        responseActions: [],
        deliverableTypes: [],
        actionOwners: []
    });
    const apiFilters = useMemo(()=>({
            status: filters.statuses.length > 0 ? filters.statuses : undefined,
            category: filters.categories.length > 0 ? filters.categories : undefined
        }), [
        filters.statuses,
        filters.categories
    ]);
    const { data, isLoading, error, refetch } = useComplianceMatrix(projectId, apiFilters);
    const updateRequirement = useUpdateRequirement(projectId);
    const [showHistory, setShowHistory] = useState(false);
    const grouped = useMemo(()=>{
        const rows = data?.requirements ?? [];
        return groupByLogicalKey(rows);
    }, [
        data?.requirements
    ]);
    const visibleRequirements = useMemo(()=>{
        if (showHistory) {
            const out = [];
            for (const key of grouped.allKeys){
                const versions = grouped.historyByKey.get(key) ?? [];
                out.push(...versions);
            }
            return out;
        }
        return grouped.currentList;
    }, [
        grouped,
        showHistory
    ]);
    // Count per action_owner tab (computed from visible requirements, before client filters)
    const tabCounts = useMemo(()=>{
        const counts = {
            PROPOSAL_ACTION: 0,
            CONTRACT_ACTION: 0,
            GOVERNMENT_ACTION: 0,
            NO_ACTION: 0
        };
        for (const req of visibleRequirements){
            const owner = req.actionOwner;
            if (owner && owner in counts) {
                counts[owner]++;
            }
        }
        return {
            PROPOSAL_ACTION: counts.PROPOSAL_ACTION,
            CONTRACT_ACTION: counts.CONTRACT_ACTION,
            GOVERNMENT_ACTION: counts.GOVERNMENT_ACTION + counts.NO_ACTION,
            ALL: visibleRequirements.length
        };
    }, [
        visibleRequirements
    ]);
    // Per-action-owner progress (addressed + partial vs total)
    const actionOwnerProgress = useMemo(()=>{
        const buckets = {
            PROPOSAL_ACTION: {
                total: 0,
                addressed: 0
            },
            CONTRACT_ACTION: {
                total: 0,
                addressed: 0
            },
            GOVERNMENT_ACTION: {
                total: 0,
                addressed: 0
            }
        };
        for (const req of visibleRequirements){
            const owner = req.actionOwner;
            const bucket = owner === 'PROPOSAL_ACTION' || owner === 'CONTRACT_ACTION' ? owner : owner === 'GOVERNMENT_ACTION' || owner === 'NO_ACTION' ? 'GOVERNMENT_ACTION' : null;
            if (!bucket) continue;
            buckets[bucket].total++;
            if (req.status === 'ADDRESSED' || req.status === 'PARTIAL') {
                buckets[bucket].addressed++;
            }
        }
        return buckets;
    }, [
        visibleRequirements
    ]);
    const filteredRequirements = useMemo(()=>{
        if (visibleRequirements.length === 0) return [];
        // Apply action_owner tab filter first
        let tabFiltered = visibleRequirements;
        if (activeTab !== 'ALL') {
            tabFiltered = visibleRequirements.filter((req)=>{
                if (activeTab === 'GOVERNMENT_ACTION') {
                    return req.actionOwner === 'GOVERNMENT_ACTION' || req.actionOwner === 'NO_ACTION';
                }
                return req.actionOwner === activeTab;
            });
        }
        // Then apply client-side filters
        const hasClientFilters = filters.searchQuery || filters.obligations.length > 0 || filters.responseActions.length > 0 || filters.deliverableTypes.length > 0 || filters.actionOwners.length > 0;
        if (!hasClientFilters) return tabFiltered;
        return tabFiltered.filter((req)=>{
            const matchesSearch = !filters.searchQuery || req.requirementId?.toLowerCase().includes(filters.searchQuery.toLowerCase()) || req.source?.exactText?.toLowerCase().includes(filters.searchQuery.toLowerCase());
            const matchesObligation = filters.obligations.length === 0 || filters.obligations.includes(req.obligationStrength);
            const matchesResponseAction = filters.responseActions.length === 0 || req.responseAction && filters.responseActions.includes(req.responseAction);
            const matchesDeliverableType = filters.deliverableTypes.length === 0 || req.deliverableType && filters.deliverableTypes.includes(req.deliverableType);
            const matchesActionOwner = filters.actionOwners.length === 0 || req.actionOwner && filters.actionOwners.includes(req.actionOwner);
            return matchesSearch && matchesObligation && matchesResponseAction && matchesDeliverableType && matchesActionOwner;
        });
    }, [
        visibleRequirements,
        activeTab,
        filters
    ]);
    // Compute groups from filtered requirements
    const requirementGroups = useMemo(()=>{
        if (groupBy === 'none') return [];
        const groupMap = new Map();
        for (const req of filteredRequirements){
            let key;
            switch(groupBy){
                case 'volume':
                    key = deriveVolume(req.mapping?.proposalSection);
                    break;
                case 'responseAction':
                    key = req.responseAction || 'Unknown';
                    break;
                case 'category':
                    key = req.category || 'Unknown';
                    break;
                default:
                    key = 'All';
            }
            const existing = groupMap.get(key);
            if (existing) {
                existing.push(req);
            } else {
                groupMap.set(key, [
                    req
                ]);
            }
        }
        return Array.from(groupMap.entries()).map(([key, reqs])=>({
                key,
                label: groupBy === 'responseAction' ? RESPONSE_ACTION_GROUP_LABELS[key] || key : key,
                requirements: reqs,
                addressedCount: reqs.filter((r)=>r.status === 'ADDRESSED' || r.status === 'PARTIAL').length
            })).sort((a, b)=>b.requirements.length - a.requirements.length);
    }, [
        filteredRequirements,
        groupBy
    ]);
    const handleExport = useCallback(async (format)=>{
        const response = await proposalProjectApiConnect.getComplianceMatrix(projectId, apiFilters);
        exportRequirements(response.requirements, format);
    }, [
        projectId,
        apiFilters
    ]);
    const handleStatusChange = useCallback((requirementId, status)=>{
        updateRequirement.mutate({
            requirementId,
            status
        });
    }, [
        updateRequirement
    ]);
    const handleAskAI = useCallback((req)=>{
        const label = req.source.exactText ? `Requirement ${req.requirementId}: ${req.source.exactText.slice(0, 120)}${req.source.exactText.length > 120 ? '…' : ''}` : `Requirement ${req.requirementId}`;
        setAiContext({
            workspace: 'compliance',
            label,
            requirementId: req.requirementId
        });
        setSidebarOpen(true);
    }, [
        setAiContext,
        setSidebarOpen
    ]);
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "space-y-6",
            children: /*#__PURE__*/ _jsx("div", {
                className: "flex items-center justify-center py-12",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col items-center gap-3",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-gray-600",
                            children: "Loading compliance matrix..."
                        })
                    ]
                })
            })
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-1 border-b border-gray-200",
                children: [
                    ACTION_TABS.map((tab)=>/*#__PURE__*/ _jsxs("button", {
                            type: "button",
                            onClick: ()=>setActiveTab(tab.key),
                            className: `px-4 py-2.5 text-sm font-medium transition-colors relative ${activeTab === tab.key ? 'text-blue-600 border-b-2 border-blue-600 -mb-px' : 'text-gray-500 hover:text-gray-700'}`,
                            title: tab.description,
                            children: [
                                tab.label,
                                /*#__PURE__*/ _jsx("span", {
                                    className: `ml-1.5 text-xs ${activeTab === tab.key ? 'text-blue-500' : 'text-gray-400'}`,
                                    children: tabCounts[tab.key]
                                })
                            ]
                        }, tab.key)),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1"
                    }),
                    /*#__PURE__*/ _jsx(ExportButton, {
                        onExport: handleExport,
                        disabled: !data?.requirements || data.requirements.length === 0
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center justify-between gap-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-3",
                        children: [
                            /*#__PURE__*/ _jsx(Filters, {
                                filters: filters,
                                updateFilters: updateFilters,
                                onClear: resetFilters
                            }),
                            /*#__PURE__*/ _jsx("select", {
                                value: groupBy,
                                onChange: (e)=>setGroupBy(e.target.value),
                                className: "rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500",
                                children: GROUP_BY_OPTIONS.map((opt)=>/*#__PURE__*/ _jsx("option", {
                                        value: opt.key,
                                        children: opt.label
                                    }, opt.key))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(HistoryToggle, {
                        currentCount: grouped.currentList.length,
                        totalCount: grouped.totalRows,
                        showHistory: showHistory,
                        onToggle: setShowHistory,
                        noun: "requirement"
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx("div", {
                className: "p-4 bg-red-50 border border-red-200 rounded-lg",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "flex items-start gap-3",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "flex-1",
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                className: "text-sm font-medium text-red-800",
                                children: "Failed to load compliance matrix"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-red-700 mt-1",
                                children: error instanceof Error ? error.message : 'An unexpected error occurred'
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                onClick: ()=>refetch(),
                                className: "mt-2 text-sm font-medium text-red-600 hover:text-red-700",
                                children: "Try again"
                            })
                        ]
                    })
                })
            }),
            !error && data && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(ComplianceStats, {
                        stats: data.statistics,
                        currentVersionCount: grouped.currentList.length,
                        totalAcrossVersions: grouped.totalRows
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "grid grid-cols-3 gap-3",
                        children: [
                            {
                                key: 'PROPOSAL_ACTION',
                                label: 'Proposal Action',
                                color: 'bg-blue-500'
                            },
                            {
                                key: 'CONTRACT_ACTION',
                                label: 'Contract Scope',
                                color: 'bg-indigo-500'
                            },
                            {
                                key: 'GOVERNMENT_ACTION',
                                label: 'Government',
                                color: 'bg-gray-400'
                            }
                        ].map(({ key, label, color })=>{
                            const { total, addressed } = actionOwnerProgress[key];
                            const pct = total > 0 ? Math.round(addressed / total * 100) : 0;
                            return /*#__PURE__*/ _jsxs("div", {
                                className: "bg-white rounded-lg border border-gray-200 px-4 py-3",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center justify-between mb-1.5",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-xs font-medium text-gray-700",
                                                children: label
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                className: "text-xs text-gray-400 tabular-nums",
                                                children: [
                                                    addressed,
                                                    "/",
                                                    total
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "h-1.5 w-full rounded-full bg-gray-100",
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: `h-1.5 rounded-full ${color} transition-all`,
                                            style: {
                                                width: `${pct}%`
                                            }
                                        })
                                    })
                                ]
                            }, key);
                        })
                    }),
                    data.evaluationScheme && data.evaluationScheme.factors.length > 0 && /*#__PURE__*/ _jsx(EvaluationSchemeCard, {
                        scheme: data.evaluationScheme
                    }),
                    groupBy !== 'none' && requirementGroups.length > 0 ? /*#__PURE__*/ _jsx("div", {
                        className: "space-y-3",
                        children: requirementGroups.map((group)=>/*#__PURE__*/ _jsx(GroupSection, {
                                group: group,
                                data: data,
                                onStatusChange: handleStatusChange,
                                updatingRequirementId: updateRequirement.variables?.requirementId || null,
                                onRowClick: setSelectedRequirement,
                                onAskAI: handleAskAI
                            }, group.key))
                    }) : /*#__PURE__*/ _jsx(ComplianceMatrixTable, {
                        data: {
                            ...data,
                            requirements: filteredRequirements
                        },
                        onStatusChange: handleStatusChange,
                        updatingRequirementId: updateRequirement.variables?.requirementId || null,
                        onRowClick: setSelectedRequirement,
                        onAskAI: handleAskAI
                    })
                ]
            }),
            selectedRequirement && /*#__PURE__*/ _jsx(ComplianceDetailDrawer, {
                requirement: selectedRequirement,
                projectId: projectId,
                isOpen: true,
                onClose: ()=>setSelectedRequirement(null)
            })
        ]
    });
});
/**
 * Collapsible group section — renders a header bar with group name, count,
 * and completion %, with a collapsible table of requirements underneath.
 */ function GroupSection({ group, data, onStatusChange, updatingRequirementId, onRowClick, onAskAI }) {
    const [isOpen, setIsOpen] = useState(true);
    const pct = group.requirements.length > 0 ? Math.round(group.addressedCount / group.requirements.length * 100) : 0;
    return /*#__PURE__*/ _jsxs("div", {
        className: "rounded-lg border border-gray-200 overflow-hidden",
        children: [
            /*#__PURE__*/ _jsxs("button", {
                type: "button",
                onClick: ()=>setIsOpen((v)=>!v),
                className: "flex w-full items-center gap-3 bg-gray-50 px-4 py-2.5 text-left hover:bg-gray-100 transition-colors",
                children: [
                    /*#__PURE__*/ _jsxs("svg", {
                        className: `h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`,
                        fill: "currentColor",
                        viewBox: "0 0 20 20",
                        children: [
                            /*#__PURE__*/ _jsx("title", {
                                children: isOpen ? 'Collapse' : 'Expand'
                            }),
                            /*#__PURE__*/ _jsx("path", {
                                d: "M6 4l8 6-8 6V4z"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-sm font-semibold text-gray-800",
                        children: group.label
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-xs text-gray-500",
                        children: group.requirements.length
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-1.5 w-20 rounded-full bg-gray-200",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "h-1.5 rounded-full bg-green-500 transition-all",
                                    style: {
                                        width: `${pct}%`
                                    }
                                })
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-[10px] text-gray-400 tabular-nums w-8 text-right",
                                children: [
                                    group.addressedCount,
                                    "/",
                                    group.requirements.length
                                ]
                            })
                        ]
                    })
                ]
            }),
            isOpen && /*#__PURE__*/ _jsx(ComplianceMatrixTable, {
                data: {
                    ...data,
                    requirements: group.requirements
                },
                onStatusChange: onStatusChange,
                updatingRequirementId: updatingRequirementId,
                onRowClick: onRowClick,
                onAskAI: onAskAI
            })
        ]
    });
}
